'use client'
import { useTranslate } from '@repo/shared/hooks'
import { useRouter } from '@repo/shared/i18n/navigation'
import { Button, Card, Col, Row, Typography } from '@repo/ui/components'
import { useAgentAddKycDocument } from '@repo/services'
import { useState } from 'react'
import { toast } from '@repo/ui/components'
import { JobQualificationsFileList } from './job-qualifications.file-list'
import { IUploadedFile } from './job-qualifications.types'
import { JobQualificationsUpload } from './job-qualifications.upload'
import { Cookie } from '@repo/tools'

const getFileExtension = (contentType: string | undefined): string => {
  if (!contentType) return ''

  const extensionMap: Record<string, string> = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      '.xlsx',
    'text/plain': '.txt',
    'text/csv': '.csv',
    'application/zip': '.zip',
    'application/x-rar-compressed': '.rar',
    'video/mp4': '.mp4',
    'video/avi': '.avi',
    'video/quicktime': '.mov',
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
  }

  return extensionMap[contentType] || ''
}

export const JobQualificationsContainer = () => {
  const { t } = useTranslate()
  const { push } = useRouter()
  const [uploadedFiles, setUploadedFiles] = useState<IUploadedFile[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // KYC mutations
  const agentAddKycDocumentMutation = useAgentAddKycDocument({
    invalidate: true,
  })

  const handleFileUpload = (files: File[]) => {
    const newFiles: IUploadedFile[] = files.map((file) => ({
      id: Math.random().toString(36).substring(2, 11),
      name: file.name,
      size: file.size,
      file,
      status: 'pending',
    }))
    setUploadedFiles((prev) => [...prev, ...newFiles])
  }

  const handleFileDelete = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId))
  }

  const handleSubmit = async () => {
    if (uploadedFiles.length === 0) {
      toast.error({
        title: t('agents_job_qualifications_no_files'),
      })
      return
    }

    setIsLoading(true)

    try {
      // Get response from sessionStorage (set by KYC page)
      const kycResponseStr = sessionStorage.getItem('kyc_response')

      if (!kycResponseStr) {
        toast.error({
          title: t('agents_job_qualifications_no_workflow'),
        })
        setIsLoading(false)
        return
      }

      // Parse the response
      let kycResponse
      try {
        kycResponse = JSON.parse(kycResponseStr)
      } catch (error) {
        console.error('Failed to parse KYC response:', error)
        toast.error({
          title: t('agents_job_qualifications_invalid_response'),
        })
        setIsLoading(false)
        return
      }

      const { workflowID, uploadLink, formData } = kycResponse

      if (!workflowID || !uploadLink) {
        toast.error({
          title: t('agents_job_qualifications_no_workflow'),
        })
        setIsLoading(false)
        return
      }

      // Step 2: Upload each file
      const fileStatuses: { [key: string]: 'success' | 'error' } = {}

      for (const fileData of uploadedFiles) {
        const rnd =
          Math.random().toString(36).substring(2, 11) +
          getFileExtension(fileData.file.type)
        try {
          // Update file status to uploading
          setUploadedFiles((prev) =>
            prev.map((file) =>
              file.id === fileData.id ? { ...file, status: 'uploading' } : file,
            ),
          )

          const _formData = new FormData()
          if (workflowID && uploadLink) {
            Object.entries(formData).forEach(([key, value]) => {
              _formData.append(key, value as string)
            })
          }
          // Note: formData from KYC response is not available here
          // We'll use the uploadLink directly without additional formData
          // _formData.append('name', formData.key + rnd)
          _formData.delete('key')
          _formData.append('key', formData.key + rnd)
          _formData.append('file', fileData.file, fileData.name)

          // Upload file
          await fetch(uploadLink, {
            method: 'POST',
            body: _formData,
          })

          // Step 3: Add document to KYC workflow
          await agentAddKycDocumentMutation.mutateAsync({
            workflowID: workflowID,
            name: formData.key + rnd,
            contentType: fileData.file.type,
          })

          // Update file status to success
          setUploadedFiles((prev) =>
            prev.map((file) =>
              file.id === fileData.id ? { ...file, status: 'success' } : file,
            ),
          )

          // Also track in local variable
          fileStatuses[fileData.id] = 'success'
        } catch (error) {
          console.error('Failed to upload file:', fileData.name, error)

          // Update file status to error
          setUploadedFiles((prev) =>
            prev.map((file) =>
              file.id === fileData.id ? { ...file, status: 'error' } : file,
            ),
          )

          // Also track in local variable
          fileStatuses[fileData.id] = 'error'

          toast.error({
            title: t('agents_job_qualifications_upload_failed'),
            description: fileData.name,
          })
        }
      }

      // Check if all files were uploaded successfully using local tracking
      const allSuccess = Object.values(fileStatuses).every(
        (status) => status === 'success',
      )

      if (allSuccess) {
        toast.success({
          title: t('agents_job_qualifications_success'),
        })
        // Clean up sessionStorage
        sessionStorage.removeItem('kyc_response')
        Cookie.set('kyc_status', 'waiting_for_review', {
          path: '/',
        })
        Cookie.set('kyc_reason', 'undefined', {
          path: '/',
        })
        Cookie.remove('kyc_required')
        Cookie.remove('kyc_mobile_or_email')

        push('/auth/success')
      } else {
        toast.error({
          title: t('agents_job_qualifications_partial_failure'),
        })
      }
    } catch (error) {
      console.error('Failed to submit job qualifications:', error)
      toast.error({
        title: t('agents_job_qualifications_submit_failed'),
      })
      // Clean up sessionStorage on error
      sessionStorage.removeItem('kyc_response')
    } finally {
      setIsLoading(false)
    }
  }

  const canSubmit = uploadedFiles.length > 0 && !isLoading

  return (
    <div className='px-md'>
      <Card className='flex flex-col !gap-3xl p-xl rounded-sm'>
        <Row className='justify-between'>
          <Typography size='2xl' weight='bold'>
            {t('agents_job_qualifications_title')}
          </Typography>
        </Row>

        <Typography size='md' className='text-neutral-surface-100 -mb-md'>
          {t('agents_job_qualifications_description')}
        </Typography>

        {uploadedFiles.length === 0 ? (
          <JobQualificationsUpload onFileUpload={handleFileUpload} />
        ) : (
          <Col className='gap-lg'>
            <JobQualificationsFileList
              files={uploadedFiles}
              onFileDelete={handleFileDelete}
              onAddMoreFiles={handleFileUpload}
            />
          </Col>
        )}

        <Button
          className='w-full'
          disabled={!canSubmit}
          onClick={handleSubmit}
          loading={isLoading}
        >
          {isLoading
            ? t('agents_job_qualifications_uploading')
            : t('agents_job_qualifications_submit')}
        </Button>
      </Card>
    </div>
  )
}
